// Block K (UI-20): one user, in the mockup's drawer (full screen on a phone). Everything the old
// detail view had: who they are, Send password reset, Remove user (super_admin, cs_manager), the
// account change form (role for super_admin; business plan; personal plan for super_admin and
// cs_manager; status; trial days; programme; reason) and the audit history with CSV export.
// Same endpoints, same payload rules. Changed: Remove is a dialog with the reason and the name
// typed (it was window.prompt); a failed audit history load is said, not "No audit history".
import React, { useCallback, useEffect, useState } from 'react';
import { api } from '../../api/client';
import { messageFromError } from '../../utils/apiError';
import { formatDate, formatDateTime } from '../../utils/formatting';
import {
  Avatar, Button, ChipGroup, EmptyState, ErrorState, Field, Input, Modal, Select, SidePanel, Skeleton, StatusPill, Textarea,
} from '../../ui';
import { actionLook, logStatusTone, planTone, programmeLabel, programmeTerms, roleLabel, statusTone, userTypeLabel } from './adminLabels';

const BUSINESS_PLANS = [
  { label: 'None', value: '' }, { label: 'Basic', value: 'Basics' }, { label: 'Pro', value: 'Pro' }, { label: 'Team Pro', value: 'Team' },
];
export const PERSONAL_PLANS = [
  { label: 'None', value: 'none' }, { label: 'Personal', value: 'Personal' }, { label: 'Family', value: 'Family' },
];

function csvEscape(value) { return `"${String(value ?? '').replace(/"/g, '""')}"`; }
function downloadCsv(filename, headers, rows) {
  const csv = [headers.map(csvEscape).join(','), ...rows.map((r) => r.map(csvEscape).join(','))].join('\n');
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
  const a = document.createElement('a');
  a.href = url; a.setAttribute('download', filename);
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function formFor(u) {
  const prog = u?.programme || null;
  return {
    role: u?.role || 'member', plan: u?.plan || '', status: u?.status || 'active', trialDays: '',
    personalTier: u?.personalTier || 'none', reason: '',
    programmeType: prog?.type || 'none',
    programmeEndsAt: prog?.endsAt ? String(prog.endsAt).slice(0, 10) : '',
    programmeDiscountPercent: Number.isFinite(prog?.discountPercent) ? String(prog.discountPercent) : '',
  };
}

export function LogLine({ log, onOpenUser }) {
  const look = actionLook(log.action);
  return (
    <li className="adm-log">
      <span className="adm-log__time">{formatDateTime(log.createdAt, undefined, { day: undefined, month: undefined, year: undefined })}</span>
      <span className="adm-tag" style={{ '--adm-tag': `var(--${look.color})` }}>{look.label}</span>
      <span className="adm-log__text">
        {log.targetUser && onOpenUser ? (
          <button type="button" className="adm-link" onClick={() => onOpenUser(log.targetUser.id)}>{log.targetUser.name || log.targetUser.email}</button>
        ) : log.targetUser ? <strong>{log.targetUser.name || log.targetUser.email}</strong> : null}
        {log.targetUser?.name && <span className="adm-muted adm-wrap"> {log.targetUser.email}</span>}
        <span className="adm-muted"> · by {log.actorUser?.name || log.actorUser?.email || 'system'}{log.approvedByUser ? `, approved by ${log.approvedByUser.name || log.approvedByUser.email}` : ''}</span>
        {log.reason && <span className="adm-log__reason">{log.reason}</span>}
      </span>
      <StatusPill tone={logStatusTone(log.status)}>{log.status}</StatusPill>
    </li>
  );
}

export default function AdminUserPanel({ user, role, metadata, grantableProgrammes, onClose, onChanged, onNotice, onResetFallback }) {
  const [form, setForm] = useState(() => formFor(user));
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [resetBusy, setResetBusy] = useState(false);
  const [logs, setLogs] = useState({ status: 'loading', list: [], error: '' });
  const [removing, setRemoving] = useState(false);
  const userId = user?.id;

  const loadLogs = useCallback(async () => {
    if (!userId) return;
    setLogs((l) => ({ ...l, status: 'loading' }));
    try {
      const res = await api(`/api/aidey-team/work-log/${userId}?limit=500`);
      const data = await res.json();
      setLogs({ status: 'ready', list: Array.isArray(data?.logs) ? data.logs : [], error: '' });
    } catch (err) {
      setLogs({ status: 'failed', list: [], error: messageFromError(err, 'The audit history could not be loaded.') });
    }
  }, [userId]);

  useEffect(() => { setForm(formFor(user)); setSaveError(''); loadLogs(); }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!user) return null;
  const set = (patch) => setForm((p) => ({ ...p, ...patch }));
  const statuses = metadata.accountStatuses.length ? metadata.accountStatuses : ['active', 'suspended', 'closed'];
  const roles = metadata.createUserRoles.length ? metadata.createUserRoles : ['member', 'admin', 'super_admin', 'cs_manager', 'cs_agent'];
  // Keep the loaded type selectable even if this admin cannot grant it, so it is not silently dropped.
  const programmeOptions = [...new Set([...(form.programmeType && form.programmeType !== 'none' ? [form.programmeType] : []), ...grantableProgrammes])];

  async function save(e) {
    e.preventDefault();
    setSaving(true); setSaveError('');
    try {
      const payload = { status: form.status };
      if (role === 'super_admin') payload.role = form.role;
      if (form.plan !== (user.plan || '')) payload.plan = form.plan;
      if (form.personalTier !== (user.personalTier || 'none')) payload.personalTier = form.personalTier;
      if (form.reason) payload.reason = form.reason;
      if (form.trialDays !== '') payload.trialDays = Number(form.trialDays);
      const loadedProg = user.programme?.type || 'none';
      if (form.programmeType !== loadedProg
        || form.programmeEndsAt !== (user.programme?.endsAt ? String(user.programme.endsAt).slice(0, 10) : '')) {
        payload.programmeType = form.programmeType;
        if (form.programmeEndsAt) payload.programmeEndsAt = new Date(form.programmeEndsAt).toISOString();
        if (form.programmeDiscountPercent !== '') payload.programmeDiscountPercent = Number(form.programmeDiscountPercent);
      }
      await api(`/api/aidey-team/users/${user.id}/account`, { method: 'PUT', body: JSON.stringify(payload) });
      // What the list then shows is read back from the server (onChanged reloads it).
      await Promise.all([onChanged(), loadLogs()]);
      setForm((p) => ({ ...p, reason: '', trialDays: '' }));
      onNotice({ tone: 'success', text: 'Account change saved and logged.' });
    } catch (err) {
      setSaveError(messageFromError(err, 'The account change was not saved.'));
    } finally { setSaving(false); }
  }

  async function sendReset() {
    setResetBusy(true);
    onResetFallback('');
    try {
      const res = await api(`/api/aidey-team/users/${user.id}/send-password-reset`, { method: 'POST' });
      const data = await res.json().catch(() => ({}));
      if (data?.emailSent === false) {
        if (data?.fallbackResetUrl) onResetFallback(data.fallbackResetUrl);
        let text;
        if (data?.reason === 'suppressed') {
          const since = data?.suppressedSince ? formatDate(data.suppressedSince) : null;
          const why = String(data?.suppressionReason || 'BOUNCE').toLowerCase();
          text = `The email could not be delivered: this address is on the SES suppression list (${why}${since ? ` since ${since}` : ''}), so it bounced before and mail to it is dropped. The mailbox may not exist. Use the fallback link, or fix the mailbox and clear the suppression entry first.`;
        } else if (data?.emailErrorCode === 'ses_recipient_not_verified') {
          text = 'The email could not be delivered because SES refused the recipient as unverified. Use the fallback link, or verify the recipient in SES.';
        } else {
          text = data?.emailErrorMessage || 'A reset link was made, but the email did not send. Use the fallback link.';
        }
        onNotice({ tone: 'problem', text });
        return;
      }
      // The route answers { ok, emailSent: true } when the email went; anything else is not a "sent".
      if (data?.emailSent !== true) {
        onNotice({ tone: 'info', text: 'The server did not say whether the email went. Check the activity log before sending another.' });
        return;
      }
      onNotice({ tone: 'success', text: `Password reset email sent to ${user.email}.` });
    } catch (err) {
      onNotice({ tone: 'problem', text: messageFromError(err, 'The password reset email was not sent.') });
    } finally { setResetBusy(false); }
  }

  const canRemove = role === 'super_admin' || role === 'cs_manager';
  const name = user.name || user.email || 'this user';

  return (
    <>
      <SidePanel open onClose={onClose} title={user.name || 'Unnamed user'} color="pastel-blue" closeLabel="Close the user">
        <div className="adm-stack" data-testid="admin-user-panel">
          <div className="adm-who">
            <Avatar name={user.name || user.email} size={64} color="pastel-blue" />
            <div className="adm-who__text">
              <span className="adm-who__name">{user.name || 'Unnamed user'}</span>
              <span className="adm-muted adm-wrap">{user.email}</span>
            </div>
          </div>
          <div className="adm-pills">
            <StatusPill tone="neutral">{roleLabel(user.role)}</StatusPill>
            <StatusPill tone={statusTone(user.status)}>{user.status || 'unknown'}</StatusPill>
            <StatusPill tone={planTone(user.effectivePlan || user.plan)}>{user.effectivePlan || user.plan || 'Trial'}</StatusPill>
            {userTypeLabel(user.userType) && <StatusPill tone="neutral">{userTypeLabel(user.userType)}</StatusPill>}
          </div>
          <dl className="adm-facts">
            <div><dt>Terms</dt><dd>{programmeTerms(user, (d) => formatDate(d))}</dd></div>
            {user.trialEndsAt && <div><dt>Trial ends</dt><dd>{formatDateTime(user.trialEndsAt)}</dd></div>}
          </dl>

          <section className="adm-section" aria-labelledby="adm-actions-h">
            <h3 id="adm-actions-h" className="adm-section__title">Actions</h3>
            <div className="adm-row-actions">
              <Button variant="tertiary" onClick={sendReset} disabled={resetBusy} aria-busy={resetBusy || undefined}>{resetBusy ? 'Sending…' : 'Send password reset email'}</Button>
              {canRemove && <Button variant="destructive" onClick={() => setRemoving(true)}>Remove user</Button>}
            </div>
          </section>

          <form className="adm-section" onSubmit={save} aria-labelledby="adm-change-h">
            <h3 id="adm-change-h" className="adm-section__title">Account change</h3>
            <div className="adm-form">
              {role === 'super_admin' && (
                <Field label="Role">
                  <Select value={form.role} onChange={(e) => set({ role: e.target.value })}>
                    {roles.map((r) => <option key={r} value={r}>{roleLabel(r)}</option>)}
                  </Select>
                </Field>
              )}
              <div className="adm-span">
                <span className="adm-chip-label" aria-hidden="true">Business plan</span>
                <ChipGroup label="Business plan" value={form.plan} onChange={(plan) => set({ plan })} options={BUSINESS_PLANS} />
              </div>
              {(role === 'super_admin' || role === 'cs_manager') && (
                <div className="adm-span">
                  <span className="adm-chip-label" aria-hidden="true">Personal plan</span>
                <ChipGroup label="Personal plan" value={form.personalTier} onChange={(personalTier) => set({ personalTier })} options={PERSONAL_PLANS} />
                </div>
              )}
              <Field label="Status">
                <Select value={form.status} onChange={(e) => set({ status: e.target.value })}>
                  {statuses.map((s) => <option key={s} value={s}>{s}</option>)}
                </Select>
              </Field>
              <Field label="Extend trial (days)">
                <Input type="number" min="0" max="365" inputMode="numeric" value={form.trialDays} onChange={(e) => set({ trialDays: e.target.value })} placeholder="For example, 14" />
              </Field>
              <Field label="Programme">
                <Select value={form.programmeType} onChange={(e) => set({ programmeType: e.target.value })}>
                  <option value="none">None</option>
                  {programmeOptions.map((t) => <option key={t} value={t}>{programmeLabel(t)}</option>)}
                </Select>
              </Field>
              {(form.programmeType === 'comp_fixed' || form.programmeType === 'trial') && (
                <Field label="Programme ends">
                  <Input type="date" value={form.programmeEndsAt} onChange={(e) => set({ programmeEndsAt: e.target.value })} />
                </Field>
              )}
              {form.programmeType === 'discount' && (
                <Field label="Discount %">
                  <Input type="number" min="0" max="100" inputMode="numeric" value={form.programmeDiscountPercent} onChange={(e) => set({ programmeDiscountPercent: e.target.value })} placeholder="For example, 25" />
                </Field>
              )}
              <Field label="Reason" className="adm-span">
                <Input value={form.reason} onChange={(e) => set({ reason: e.target.value })} placeholder="Why is this change being made?" />
              </Field>
            </div>
            {saveError && <p className="adm-error" role="alert">{saveError}</p>}
            <div className="adm-row-actions adm-row-actions--end">
              <Button type="submit" variant="confirm" disabled={saving} aria-busy={saving || undefined}>{saving ? 'Saving…' : 'Save and log change'}</Button>
            </div>
          </form>

          <section className="adm-section" aria-labelledby="adm-audit-h">
            <div className="adm-section__head">
              <h3 id="adm-audit-h" className="adm-section__title">Audit history{logs.status === 'ready' ? ` (${logs.list.length})` : ''}</h3>
              <Button size="sm" variant="tertiary" disabled={logs.status !== 'ready' || logs.list.length === 0} onClick={() => {
                const headers = ['Log ID', 'Created At', 'Action', 'Status', 'Actor Name', 'Actor Email', 'Approver Name', 'Approver Email', 'Reason', 'Metadata'];
                const rows = logs.list.map((log) => [log.id, log.createdAt, log.action, log.status, log.actorUser?.name || '', log.actorUser?.email || '', log.approvedByUser?.name || '', log.approvedByUser?.email || '', log.reason || '', JSON.stringify(log.metadata || {})]);
                const safe = String(user.email || user.name || 'user').replace(/[^a-zA-Z0-9._-]/g, '_');
                downloadCsv(`aidey-audit-${safe}-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.csv`, headers, rows);
              }}>Export CSV</Button>
            </div>
            {logs.status === 'loading' ? <Skeleton lines={3} label="Loading the audit history" />
              : logs.status === 'failed' ? <ErrorState title="The audit history did not load" message={logs.error} onRetry={loadLogs} />
              : logs.list.length === 0 ? <EmptyState icon="🗒️" title="Nothing logged for this user">Changes made here are logged and appear in this list.</EmptyState>
              : <ul className="adm-logs adm-logs--panel">{logs.list.map((log) => <LogLine key={log.id} log={log} />)}</ul>}
          </section>
        </div>
      </SidePanel>
      {removing && <RemoveUserDialog user={user} name={name} onClose={() => setRemoving(false)} onRemoved={async () => { setRemoving(false); onNotice({ tone: 'success', text: `${name} has been permanently removed.` }); onClose(); await onChanged(); }} />}
    </>
  );
}

// Decision 53: no Undo, so the name is typed. The server requires the reason.
function RemoveUserDialog({ user, name, onClose, onRemoved }) {
  const [reason, setReason] = useState('');
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const ready = reason.trim() && typed === name && !busy;
  async function remove() {
    if (!ready) return;
    setBusy(true); setError('');
    try {
      await api(`/api/aidey-team/users/${user.id}`, { method: 'DELETE', body: JSON.stringify({ reason: reason.trim() }) });
      await onRemoved();
    } catch (err) {
      setError(messageFromError(err, 'The user was not removed. Nothing was changed.'));
      setBusy(false);
    }
  }
  return (
    <Modal open onClose={busy ? undefined : onClose} title={`Remove ${name}?`} color="pastel-coral" width={480}
      footer={<>
        <Button variant="tertiary" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="delete" onClick={remove} disabled={!ready} aria-busy={busy || undefined}>{busy ? 'Removing…' : 'Remove'}</Button>
      </>}>
      <div className="adm-stack">
        <p className="adm-lead">This permanently removes <strong>{name}</strong> ({user.email}). It cannot be undone.</p>
        <Field label="Reason" required hint="Logged with the removal.">
          <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} />
        </Field>
        <Field label={`Type ${name} to confirm`}>
          <Input value={typed} onChange={(e) => setTyped(e.target.value)} autoComplete="off" spellCheck={false} />
        </Field>
        {error && <p className="adm-error" role="alert">{error}</p>}
      </div>
    </Modal>
  );
}
